import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AutoritaGiudiziaria } from 'src/app/common/models/tipologiche/autorita-giudiziaria';
import { Comune } from 'src/app/common/models/tipologiche/comune';
import { Grado } from 'src/app/common/models/tipologiche/grado';
import { ElaborationDocumentFilterEntity } from 'src/app/document-management/models/elaboration-doc-filter';
import {
  ElaborationEntityConstants
} from 'src/app/pratica/components/wizard-creazione/constants/elaboration-entity-constants';
import {
  CreazionePraticaTipologicheService
} from 'src/app/pratica/components/wizard-creazione/services/creazione-pratica-tipologiche.service';
import { filterElaborationEntityClean } from 'src/app/pratica/components/wizard-creazione/utils/wizard-utils';
import { OperationsService } from 'src/app/core/services/operations.service';
import { HelpersService } from 'src/app/core/services/helpers.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-dati-generali-fascicolo-giudiziale-common',
  templateUrl: './dati-generali-fascicolo-giudiziale-common.component.html',
  styleUrls: ['./dati-generali-fascicolo-giudiziale-common.component.css']
})
export class DatiGeneraliFascicoloGiudizialeCommonComponent implements OnInit, OnDestroy {

  @Input() request;
  @Input() fascicolo;
  @Input() inserimento;
  @Input() datiGeneraliForm: FormGroup;
  @Input() entities: ElaborationDocumentFilterEntity[];

  currentUrl: string;
  private gradi: Grado[] = [];
  private autoritaGiudiziarie: AutoritaGiudiziaria[] = [];
  private cittaPerAutoritaGiudiziaria: Comune[] = [];
  componentDestroyed$: Subject<void> = new Subject<void>();

  numAndAnnoRuoloSuggested: ElaborationDocumentFilterEntity[];
  autGiudiziariaSuggested : ElaborationDocumentFilterEntity[];
  sectionSuggested : ElaborationDocumentFilterEntity[];
  judgeSuggested : ElaborationDocumentFilterEntity[];
  sedeSuggested : boolean;
  idTipoPratica: string;

  constructor(private tipologicheService: CreazionePraticaTipologicheService, private router: Router, private ops: OperationsService, private helpers: HelpersService) { }

    ngOnInit() {
      // carico i gradi
      if (this.gradi.length === 0) {
        this.tipologicheService.getGradi().pipe(takeUntil(this.componentDestroyed$))
          .subscribe(response => this.gradi = response);
      }

      // carico tipiPratica e lo salvo in idTipoPratica; solo dopo carico autorita tenendo conto se siamo in modifica
      this.tipologicheService.getTipiPratica().pipe(takeUntil(this.componentDestroyed$)).subscribe(elem => {
        const tipiPratica = elem;
        const tipoPraticaLabel = this.checkRoute(); // rimappo la logica di route in helper
        const found = tipiPratica.filter(p => p.label === tipoPraticaLabel)[0];
        this.idTipoPratica = found ? found.id : undefined;

        // ora che idTipoPratica è pronto, carico autorita (filtrate se possibile)
        this.loadAutoritaGiudiziarieOnInit();
      });

      // chiamata per città
      this.callCittaPerAutoritaGiudiziaria(null);
  }

  private checkRoute(): string {
    if(this.router.url.includes('civile')) return "CIVILE";
    if(this.router.url.includes('pareristica')) return "PARERISTIVA";
    if(this.router.url.includes('amministrativa')) return "AMMINISTRATIVA";
    if(this.router.url.includes('lavoro')) return "LAVORO";
    if(this.router.url.includes('penale')) return "PENALE";
    if(this.router.url.includes('tributario')) return "TRIBUTARIO";
    return "";
  }

  private loadAutoritaGiudiziarieOnInit() {
    // Se siamo in categoria GIUDIZIALE e c'è già un grado definito nel fascicolo,
    // richiamiamo la API che ritorna le autorità correlate
    const isGiudiziale = this.fascicolo?.tipoFascicolo?.categoria?.id === 'GIUDIZIALE' || this.request?.fascicolo?.tipoFascicolo?.categoria?.id === 'GIUDIZIALE';

    const gradoId = this.fascicolo?.grado?.id || this.request?.fascicolo?.grado?.id;

    if (isGiudiziale && gradoId && this.idTipoPratica) {
      this.helpers.getAutoritaGiudiziarieByFascicoloGrado(this.idTipoPratica, this.fascicolo.tipoFascicolo.id, gradoId)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(response => {
          this.autoritaGiudiziarie = response;
          // dopo aver caricato le autorità filtrate, eseguo preselezioni
          this.preselezionaNumAnnoRuolo();
          this.preselezionaAutGiudiziaria();
          this.preselezionaCourtSection();
          this.preselezionaJudge();
        }, err => {
          // in caso di errore fallback a tutte le autorità
          this.tipologicheService.getAutoritaGiudiziarie().pipe(takeUntil(this.componentDestroyed$)).subscribe(res => this.autoritaGiudiziarie = res);
        });
    } else {
      // fallback: carico tutte le autorità (inserimento o mancati dati)
      this.tipologicheService.getAutoritaGiudiziarie().pipe(takeUntil(this.componentDestroyed$)).subscribe(response => {
        this.autoritaGiudiziarie = response;
        // poi le preselezioni usuali
        this.preselezionaNumAnnoRuolo();
        this.preselezionaAutGiudiziaria();
        this.preselezionaCourtSection();
        this.preselezionaJudge();
      });
    }
  }

  private callCittaPerAutoritaGiudiziaria(autGiudiziariaAndSede: string) {
    if (this.request.fascicolo && this.request.fascicolo.autoritaGiudiziaria && this.request.fascicolo.autoritaGiudiziaria.id) {
      this.tipologicheService.getCittaPerAutoritaGiudiziaria(this.request.fascicolo.autoritaGiudiziaria.id).subscribe(response => {
        this.cittaPerAutoritaGiudiziaria = response;
        if(autGiudiziariaAndSede && autGiudiziariaAndSede.length > 0){
          this.request.fascicolo.autoritaGiudiziariaCitta = this.cittaPerAutoritaGiudiziaria.filter(elem => elem.label.toLowerCase().includes(autGiudiziariaAndSede.toLowerCase()) ||
           autGiudiziariaAndSede.toLowerCase().includes(elem.descrizione.toLowerCase()))[0];
          this.sedeSuggested = !!this.request.fascicolo.autoritaGiudiziariaCitta;
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
  }

  // ngOnChanges(changes: SimpleChanges) {
  //   this.preselezionaNumAnnoRuolo();
  //   this.preselezionaAutGiudiziaria();
  //   this.preselezionaCourtSection();
  //   this.preselezionaJudge();
  // }

  private preselezionaNumAnnoRuolo(){
    if(this.entities&& this.entities.length > 0){
      this.numAndAnnoRuoloSuggested = filterElaborationEntityClean(this.entities, [ElaborationEntityConstants.CASE_NUMBER]);
      if(this.numAndAnnoRuoloSuggested &&
        this.numAndAnnoRuoloSuggested.length > 0){
          let str = this.numAndAnnoRuoloSuggested[0].valore;
          this.fascicolo.numeroRuolo = str.slice(0,str.indexOf("/"));
          let anno = str.slice(str.indexOf("/")+1);
          this.fascicolo.annoRuolo = anno.length == 4 ? anno : "20"+anno;
        }
    }
  }

  private preselezionaAutGiudiziaria() {
    if (this.entities && this.entities.length > 0) {
      this.autGiudiziariaSuggested = filterElaborationEntityClean(this.entities, [ElaborationEntityConstants.AUT_GIUDIZIARIA]);
      if(this.autGiudiziariaSuggested && this.autGiudiziariaSuggested.length > 0){
        this.sedeSuggested = this.autGiudiziariaSuggested[0].valore.includes('sede:')
      }
      if (!this.fascicolo.autoritaGiudiziaria &&
        this.autGiudiziariaSuggested &&
        this.autGiudiziariaSuggested.length > 0 &&
        this.autoritaGiudiziarie.length > 0) {
          this.autGiudiziariaSuggested.sort((aut1,aut2) => aut2.valore.length - aut1.valore.length);

          let autGiudEntityFiltered = this.autGiudiziariaSuggested[0];
          let autFind = this.autoritaGiudiziarie.filter(aut => aut.descrizione.toLowerCase().includes(autGiudEntityFiltered.valore.toLowerCase()) ||
          autGiudEntityFiltered.valore.toLowerCase().includes(aut.descrizione.toLowerCase()));
          if (autFind && autFind.length > 0) {
            this.fascicolo.autoritaGiudiziaria = autFind[0];
            this.callCittaPerAutoritaGiudiziaria(autGiudEntityFiltered.valore);
          }
      }
    }
  }

  private preselezionaCourtSection(){
    if (this.entities && this.entities.length > 0) {
      this.sectionSuggested = filterElaborationEntityClean(this.entities, [ElaborationEntityConstants.COURT_SECTION]);
      if(this.sectionSuggested && this.sectionSuggested.length > 0){
        this.fascicolo.sezione = this.sectionSuggested[0].valore;
      }
    }
  }

  private preselezionaJudge(){
    if (this.entities && this.entities.length > 0) {
      this.judgeSuggested = filterElaborationEntityClean(this.entities, [ElaborationEntityConstants.JUDGE]);
      if(this.judgeSuggested && this.judgeSuggested.length > 0){
        this.fascicolo.giudice = this.judgeSuggested[0].valore;
      }
    }
  }


  loadCittaPerAutoritaGiudiziaria(event: any) {
    this.cittaPerAutoritaGiudiziaria = [];
    this.request.fascicolo.autoritaGiudiziariaCitta = undefined;
    let idAutorita = event.target.value;
    if (idAutorita !== '-1') {
      this.tipologicheService.getCittaPerAutoritaGiudiziaria(idAutorita).pipe(takeUntil(this.componentDestroyed$)).subscribe(response => this.cittaPerAutoritaGiudiziaria = response);
    }
  }

  onChangeGrado(event: any){
    this.request.fascicolo.grado = event;
    const isGiudiziale = this.request.fascicolo.tipoFascicolo?.categoria?.id === 'GIUDIZIALE' || this.fascicolo.tipoFascicolo?.categoria?.id === 'GIUDIZIALE';

    const doLoadAutorita = (idTipoPraticaValue: string) => {
      if(event?.id){
        this.helpers.getAutoritaGiudiziarieByFascicoloGrado(idTipoPraticaValue, this.fascicolo.tipoFascicolo.id, event.id)
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe(response =>{
            this.autoritaGiudiziarie = response;
            this.cittaPerAutoritaGiudiziaria = [];
            this.request.fascicolo.autoritaGiudiziariaCitta = undefined;
            this.preselezionaNumAnnoRuolo();
            this.preselezionaAutGiudiziaria();
            this.preselezionaCourtSection();
            this.preselezionaJudge();
          });
      } else {
        this.tipologicheService.getAutoritaGiudiziarie().pipe(takeUntil(this.componentDestroyed$))
          .subscribe(response =>{
            this.autoritaGiudiziarie = response;
            this.cittaPerAutoritaGiudiziaria = [];
            this.request.fascicolo.autoritaGiudiziariaCitta = undefined; 
            this.preselezionaNumAnnoRuolo();
            this.preselezionaAutGiudiziaria();
            this.preselezionaCourtSection();
            this.preselezionaJudge();
          } );
      }
    };

    if (!isGiudiziale) return; // se non è giudiziale non si fa altro

    if (this.idTipoPratica) {
      doLoadAutorita(this.idTipoPratica);
    } else {
      // idTipoPratica non ancora pronto: carichiamolo e poi richiamiamo
      this.tipologicheService.getTipiPratica().pipe(takeUntil(this.componentDestroyed$)).subscribe(elem => {
        const tipiPratica = elem;
        const tipoPraticaLabel = this.checkRoute();
        const found = tipiPratica.filter(p => p.label === tipoPraticaLabel)[0];
        this.idTipoPratica = found ? found.id : undefined;
        if (this.idTipoPratica) doLoadAutorita(this.idTipoPratica);
        else this.tipologicheService.getAutoritaGiudiziarie().pipe(takeUntil(this.componentDestroyed$)).subscribe(r => this.autoritaGiudiziarie = r);
      });
    }
  }

}
